import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Block as BlockIcon,
  Api as ApiIcon,
  Visibility as VisibilityIcon,
  TableChart as TableChartIcon,
  ViewKanban as ViewKanbanIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../../store/Index";
import type { EndpointRequest, RequestStatus, Priority, HttpMethod } from "./types";
import apiService, { BaseURL } from "../../service/Api";
import {
  setRequests,
  updateRequest as updateRequestAction,
  deleteRequest as deleteRequestAction,
  setLoading,
  setError,
} from "../../store/slice/EndpointRequestsSlice";
import { CreateRequestDialog } from "./components/CreateRequestDialog";
import { ViewRequestDialog } from "./components/ViewRequestDialog";
import { DeveloperStatsComponent } from "./components/DeveloperStats";
import { AdminDashboard } from "./components/AdminDashboard";
import { BackendDashboard } from "./components/BackendDashboard";
import { notifyRequestCreated } from "./utils/notifications";
import { isAdmin, isBackendDev, isFrontendDev } from "./utils/permissions";
import {
  ModernStatusBadge,
  ModernMethodChip,
  ModernPriorityChip,
  ModernStatCard,
} from "./components/ModernUiComponents";
import { EndpointKanbanView } from "./components/EndpointKanbanView";
import { CodeSnippetDialog } from "./components/CodeSnippetDialog";

interface EndpointRequestApiItem {
  id: number;
  component_name_snapshot: string;
  task_description: string;
  requested_by: {
    username: string;
    first_name: string;
    last_name: string;
    email?: string;
  };
  assigned_backend?: {
    id: number;
    username: string;
    email: string;
    first_name?: string;
    last_name?: string;
  } | null;
  assigned_backend_email?: string | null;
  endpoint_url: string;
  http_method: string;
  status: string;
  priority: string;
  request_date_time: string;
  target_delivery_date_time: string | null;
  expected_delivery_date?: string | null;
  payload: Record<string, unknown> | null;
  additional_information: string;
  actual_completion_date?: string;
  status_code?: number;
  challenges?: string;
  endpoint_screenshot_image?: string | null;
}

type EndpointRequestsApiResponse =
  | EndpointRequestApiItem[]
  | {
      results: EndpointRequestApiItem[];
    };

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      detail?: string;
      [key: string]: unknown;
    };
  };
}

const EndpointRequestsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);
  const requests = useSelector((state: RootState) => state.endpointRequests.requests);

  const [viewMode, setViewMode] = useState<"table" | "kanban">("table");
  const [filteredRequests, setFilteredRequests] = useState<EndpointRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<RequestStatus | "all">("all");
  const [priorityFilter, setPriorityFilter] = useState<Priority | "all">("all");
  const [componentFilter, setComponentFilter] = useState<string>("");
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<EndpointRequest | null>(null);
  const [codeSnippetRequest, setCodeSnippetRequest] = useState<EndpointRequest | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "pending" | "completed">("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    blocked: 0,
  });

  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";
  const location = useLocation();

  const updateNotificationCount = React.useCallback(() => {
    const pendingCount = requests.filter(
      (r) => r.status === "pending" || r.status === "in_progress"
    ).length;
    localStorage.setItem("endpoint_requests_notification_count", pendingCount.toString());
    window.dispatchEvent(new CustomEvent("endpoint-requests-updated"));
  }, [requests]);

  const loadRequests = React.useCallback(async () => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await apiService.get<EndpointRequestsApiResponse>(
        "/api/endpoint-request/endpoint-requests/"
      );

      const dataArray: EndpointRequestApiItem[] = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];

      const mappedRequests: EndpointRequest[] = dataArray.map((item) => {
        const extractDate = (dateString: string | null | undefined): string | undefined => {
          if (!dateString) return undefined;
          try {
            return dateString.split("T")[0];
          } catch {
            return undefined;
          }
        };

        return {
          id: item.id.toString(),
          component: item.component_name_snapshot,
          task: item.task_description,
          lead: item.requested_by?.first_name || item.requested_by?.username || "Unknown",
          endpoint: item.endpoint_url || undefined,
          method: (item.http_method as HttpMethod) || undefined,
          status: item.status as RequestStatus,
          priority: item.priority as Priority,
          requestDate: extractDate(item.request_date_time) || new Date().toISOString().split("T")[0],
          targetDeliveryDate: extractDate(item.target_delivery_date_time),
          expectedDeliveryDate: extractDate(item.expected_delivery_date),
          actualCompletionDate: extractDate(item.actual_completion_date),
          payload: item.payload ? JSON.stringify(item.payload, null, 2) : undefined,
          description: item.additional_information || undefined,
          createdBy: item.requested_by?.username || "Unknown",
          statusCode: item.status_code,
          challenges: item.challenges,
          assignedBackend: item.assigned_backend || null,
          assignedBackendId: item.assigned_backend?.id || null,
          assignedBackendEmail: item.assigned_backend_email || item.assigned_backend?.email || null,
          imageUrl: item.endpoint_screenshot_image
            ? item.endpoint_screenshot_image.startsWith("http")
              ? item.endpoint_screenshot_image
              : `${BaseURL.replace(/\/$/, "").replace(/\/api\/?$/, "")}${item.endpoint_screenshot_image.startsWith("/") ? "" : "/"}${item.endpoint_screenshot_image}`
            : undefined,
        };
      });

      dispatch(setRequests(mappedRequests));
    } catch (error: unknown) {
      console.error("Error loading requests:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to load endpoint requests";
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
      dispatch(setRequests([]));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const filterRequests = React.useCallback(() => {
    let filtered = [...requests];

    if (activeTab === "pending") {
      filtered = filtered.filter((r) => r.status === "pending" || r.status === "in_progress");
    } else if (activeTab === "completed") {
      filtered = filtered.filter((r) => r.status === "completed");
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    if (priorityFilter !== "all") {
      filtered = filtered.filter((r) => r.priority === priorityFilter);
    }

    if (componentFilter) {
      filtered = filtered.filter((r) =>
        r.component.toLowerCase().includes(componentFilter.toLowerCase())
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.component.toLowerCase().includes(term) ||
          r.task.toLowerCase().includes(term) ||
          r.lead.toLowerCase().includes(term) ||
          r.endpoint?.toLowerCase().includes(term)
      );
    }

    setFilteredRequests(filtered);
  }, [requests, activeTab, statusFilter, priorityFilter, componentFilter, searchTerm]);

  useEffect(() => {
    loadRequests().catch((error) => {
      console.error("Failed to load requests:", error);
    });
  }, [loadRequests]);

  useEffect(() => {
    if (requests.length > 0) {
      updateNotificationCount();
    }
  }, [requests, updateNotificationCount]);

  useEffect(() => {
    filterRequests();
  }, [filterRequests]);

  const handleCreateRequest = async (request: Omit<EndpointRequest, "id">) => {
    setOpenCreateDialog(false);
    await loadRequests();

    notifyRequestCreated({
      id: "new",
      component: request.component,
      task: request.task,
      createdBy: user?.username || "Unknown",
    });

    toast.info(`New endpoint request created: ${request.component}`);
    window.dispatchEvent(new CustomEvent("endpoint-request-created", {
      detail: { username: user?.username }
    }));
  };

  const handleUpdateRequest = async (id: string, updates: Partial<EndpointRequest>) => {
    const request = requests.find((r) => r.id === id);
    try {
      const apiPayload: Record<string, unknown> = {};

      if (updates.task !== undefined) apiPayload.task_description = updates.task;
      if (updates.method !== undefined) apiPayload.http_method = updates.method;
      if (updates.priority !== undefined) apiPayload.priority = updates.priority;
      if (updates.status !== undefined) apiPayload.status = updates.status;
      if (updates.endpoint !== undefined) apiPayload.endpoint_url = updates.endpoint;
      if (updates.description !== undefined) apiPayload.additional_information = updates.description;
      if (updates.statusCode !== undefined) apiPayload.status_code = updates.statusCode;
      if (updates.challenges !== undefined) apiPayload.challenges = updates.challenges;

      if (updates.targetDeliveryDate !== undefined) {
        apiPayload.target_delivery_date_time = updates.targetDeliveryDate
          ? new Date(updates.targetDeliveryDate).toISOString()
          : null;
      }

      if (updates.expectedDeliveryDate !== undefined) {
        apiPayload.expected_delivery_date = updates.expectedDeliveryDate
          ? new Date(updates.expectedDeliveryDate).toISOString()
          : null;
      }

      if (updates.actualCompletionDate !== undefined) {
        apiPayload.actual_completion_date = updates.actualCompletionDate
          ? new Date(updates.actualCompletionDate).toISOString()
          : null;
      }

      if (updates.payload !== undefined) {
        if (updates.payload && updates.payload.trim()) {
          try {
            apiPayload.payload = JSON.parse(updates.payload);
          } catch {
            apiPayload.payload = updates.payload;
          }
        } else {
          apiPayload.payload = null;
        }
      }

      await apiService.patch(`/api/endpoint-request/endpoint-requests/${id}/`, apiPayload);
      dispatch(updateRequestAction({ id, updates }));
      await loadRequests();

      toast.success("Endpoint request updated successfully");

      if (updates.status === "completed" && request?.status !== "completed") {
        toast.success(`Endpoint request completed: ${request?.component}`);
      }
    } catch (error: unknown) {
      console.error("Failed to update endpoint request:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to update endpoint request";
      toast.error(errorMessage);
    }
  };

  const handleDeleteRequest = async (id: string) => {
    try {
      await apiService.delete(`/api/endpoint-request/endpoint-requests/${id}/`);
      dispatch(deleteRequestAction(id));
      setOpenViewDialog(false);
      setSelectedRequest(null);
      await loadRequests();
      toast.success("Endpoint request deleted successfully");
    } catch (error: unknown) {
      console.error("Failed to delete endpoint request:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to delete endpoint request";
      toast.error(errorMessage);
    }
  };

  const handleViewRequest = (request: EndpointRequest) => {
    setSelectedRequest(request);
    setOpenViewDialog(true);
  };

  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const userIsAdmin = isAdmin(user);
  const userIsBackendDev = isBackendDev(user);
  const userIsFrontendDev = isFrontendDev(user);

  const routePath = location.pathname || "";
  let renderAdmin = false;
  let renderBackend = false;
  let renderFrontend = false;

  if (routePath.endsWith("/frontend")) {
    renderFrontend = userIsFrontendDev || userIsAdmin;
  } else if (routePath.endsWith("/backend")) {
    renderBackend = userIsBackendDev || userIsAdmin;
  } else {
    if (userIsAdmin) renderAdmin = true;
    else if (userIsFrontendDev) renderFrontend = true;
    else if (userIsBackendDev) renderBackend = true;
  }

  const userCanSeeFrontend = userIsFrontendDev || userIsAdmin;

  const loadStats = React.useCallback(async () => {
    if (!userCanSeeFrontend) return;
    try {
      const response = await apiService.get<{
        total_requests: number;
        status_counts: {
          completed?: number;
          pending?: number;
          in_progress?: number;
          blocked?: number;
          cancelled?: number;
        };
      }>("/api/endpoint-request/endpoint-requests/statistics/");

      setStats({
        total: response.data.total_requests || 0,
        pending: (response.data.status_counts.pending || 0) + (response.data.status_counts.in_progress || 0),
        completed: response.data.status_counts.completed || 0,
        blocked: response.data.status_counts.blocked || 0,
      });
    } catch (error: unknown) {
      console.error("Error loading stats:", error);
      setStats({
        total: requests.length,
        pending: requests.filter((r) => r.status === "pending" || r.status === "in_progress").length,
        completed: requests.filter((r) => r.status === "completed").length,
        blocked: requests.filter((r) => r.status === "blocked").length,
      });
    }
  }, [userCanSeeFrontend, requests]);

  useEffect(() => {
    if (userCanSeeFrontend) {
      loadStats();
    }
  }, [userCanSeeFrontend, loadStats]);

  useEffect(() => {
    if (userCanSeeFrontend && requests.length > 0) {
      loadStats();
    }
  }, [requests.length, userCanSeeFrontend, loadStats]);

  const hasAccessToCurrentRoute =
    (routePath.endsWith("/frontend") && (userIsFrontendDev || userIsAdmin)) ||
    (routePath.endsWith("/backend") && (userIsBackendDev || userIsAdmin)) ||
    ((routePath === "/endpoint-requests" || routePath === "/endpoint-requests/") && (userIsAdmin || userIsFrontendDev || userIsBackendDev));

  if (!hasAccessToCurrentRoute) {
    return (
      <Box sx={{ p: 4, backgroundColor: "background.default", minHeight: "100vh" }}>
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography variant="h5" color="error" fontWeight="800">
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You don&apos;t have permission to view this endpoint portal view.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3.5 }, backgroundColor: "background.default", minHeight: "100vh" }}>
      {renderAdmin && !renderBackend && !renderFrontend && (
        <AdminDashboard
          requests={requests}
          onRequestUpdate={handleUpdateRequest}
          onViewRequest={handleViewRequest}
        />
      )}

      {renderBackend && !renderAdmin && !renderFrontend && (
        <BackendDashboard
          requests={requests}
          onRequestUpdate={handleUpdateRequest}
          onReloadRequests={loadRequests}
          onViewRequest={handleViewRequest}
        />
      )}

      {renderFrontend && !renderAdmin && !renderBackend && (
        <>
          {/* Hero Header */}
          <Paper
            elevation={0}
            sx={{
              p: 3.5,
              mb: 3.5,
              borderRadius: "20px",
              background: `linear-gradient(135deg, ${primaryColor}1F 0%, ${primaryColor}0D 100%)`,
              border: `1px solid ${primaryColor}30`,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.04)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
              <Box
                sx={{
                  p: 1.8,
                  borderRadius: "16px",
                  backgroundColor: primaryColor,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 8px 20px ${primaryColor}40`,
                }}
              >
                <ApiIcon sx={{ fontSize: 34 }} />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="800" sx={{ color: "text.primary", letterSpacing: "-0.5px" }}>
                  Developer Portal
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  Request, manage, and track API endpoint contracts with backend engineering
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* View Switcher Toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, nextView) => nextView && setViewMode(nextView)}
                sx={{
                  backgroundColor: "background.paper",
                  borderRadius: "12px",
                  p: 0.5,
                  border: "1px solid rgba(226, 232, 240, 0.9)",
                }}
              >
                <ToggleButton
                  value="table"
                  sx={{
                    borderRadius: "8px",
                    px: 1.75,
                    py: 0.6,
                    fontWeight: 700,
                    textTransform: "none",
                    gap: 0.75,
                  }}
                >
                  <TableChartIcon fontSize="small" />
                  Table
                </ToggleButton>
                <ToggleButton
                  value="kanban"
                  sx={{
                    borderRadius: "8px",
                    px: 1.75,
                    py: 0.6,
                    fontWeight: 700,
                    textTransform: "none",
                    gap: 0.75,
                  }}
                >
                  <ViewKanbanIcon fontSize="small" />
                  Board
                </ToggleButton>
              </ToggleButtonGroup>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                sx={{
                  backgroundColor: primaryColor,
                  color: "white",
                  "&:hover": {
                    backgroundColor: primaryColor,
                    opacity: 0.92,
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 20px ${primaryColor}50`,
                  },
                  borderRadius: "12px",
                  px: 3,
                  py: 1.25,
                  fontWeight: 700,
                  textTransform: "none",
                  transition: "all 0.2s ease",
                  boxShadow: `0 4px 12px ${primaryColor}35`,
                }}
              >
                New Endpoint Request
              </Button>
            </Box>
          </Paper>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3.5 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <ModernStatCard
                title="Total Requests"
                value={stats.total}
                icon={<ApiIcon sx={{ fontSize: 28 }} />}
                accentColor={primaryColor}
                subtext="All frontend API contracts"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <ModernStatCard
                title="Pending / In Progress"
                value={stats.pending}
                icon={<PendingIcon sx={{ fontSize: 28 }} />}
                accentColor="#F59E0B"
                subtext="Awaiting backend completion"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <ModernStatCard
                title="Completed & Active"
                value={stats.completed}
                icon={<CheckCircleIcon sx={{ fontSize: 28 }} />}
                accentColor="#10B981"
                subtext="Available API endpoints"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <ModernStatCard
                title="Blocked Requests"
                value={stats.blocked}
                icon={<BlockIcon sx={{ fontSize: 28 }} />}
                accentColor="#EF4444"
                subtext="Requires resolution"
              />
            </Grid>
          </Grid>

          {/* Gamification Stats Component */}
          {user?.username && (
            <DeveloperStatsComponent requests={requests} currentUser={user.username} />
          )}

          {/* Filters Bar */}
          <Paper
            elevation={0}
            sx={{
              mb: 3.5,
              borderRadius: "16px",
              border: "1px solid rgba(226, 232, 240, 0.8)",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.02)",
              backgroundColor: "background.paper",
              overflow: "hidden",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => {
                setActiveTab(newValue);
                setPage(0);
              }}
              sx={{
                px: 2,
                pt: 1,
                borderBottom: "1px solid #E2E8F0",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  minHeight: 52,
                  px: 3,
                  borderRadius: "10px 10px 0 0",
                  color: "text.secondary",
                  "&.Mui-selected": {
                    color: primaryColor,
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: primaryColor,
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    All Requests
                    <Chip
                      label={stats.total}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        backgroundColor: activeTab === "all" ? `${primaryColor}20` : "#F1F5F9",
                        color: activeTab === "all" ? primaryColor : "#64748B",
                      }}
                    />
                  </Box>
                }
                value="all"
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Pending
                    <Chip
                      label={stats.pending}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        backgroundColor: activeTab === "pending" ? "#FFFBEB" : "#F1F5F9",
                        color: activeTab === "pending" ? "#B45309" : "#64748B",
                      }}
                    />
                  </Box>
                }
                value="pending"
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Completed
                    <Chip
                      label={stats.completed}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        backgroundColor: activeTab === "completed" ? "#ECFDF5" : "#F1F5F9",
                        color: activeTab === "completed" ? "#047857" : "#64748B",
                      }}
                    />
                  </Box>
                }
                value="completed"
              />
            </Tabs>

            <Box sx={{ p: 2.5 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    placeholder="Search by component, task, lead, or endpoint..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(0);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => {
                        setStatusFilter(e.target.value as RequestStatus | "all");
                        setPage(0);
                      }}
                      sx={{ borderRadius: "12px" }}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="blocked">Blocked</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={priorityFilter}
                      label="Priority"
                      onChange={(e) => {
                        setPriorityFilter(e.target.value as Priority | "all");
                        setPage(0);
                      }}
                      sx={{ borderRadius: "12px" }}
                    >
                      <MenuItem value="all">All Priorities</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Component..."
                    value={componentFilter}
                    onChange={(e) => {
                      setComponentFilter(e.target.value);
                      setPage(0);
                    }}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => {
                      setStatusFilter("all");
                      setPriorityFilter("all");
                      setComponentFilter("");
                      setSearchTerm("");
                      setPage(0);
                    }}
                    sx={{
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 600,
                      py: 0.8,
                      borderColor: "#CBD5E1",
                      color: "text.secondary",
                      "&:hover": { borderColor: "#94A3B8" },
                    }}
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* VIEW RENDER: Kanban Board vs Table */}
          {viewMode === "kanban" ? (
            <EndpointKanbanView
              requests={filteredRequests}
              onViewRequest={handleViewRequest}
              onOpenCodeSnippet={(req) => setCodeSnippetRequest(req)}
              onStatusChange={(id, status) => handleUpdateRequest(id, { status })}
              primaryColor={primaryColor}
            />
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: "18px",
                border: "1px solid rgba(226, 232, 240, 0.9)",
                boxShadow: "0 4px 20px -2px rgba(0,0,0,0.04)",
                overflow: "hidden",
                backgroundColor: "background.paper",
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Component</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Task Description</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Lead</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Assigned Dev</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Endpoint Contract</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Priority</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                          <Typography variant="body1" fontWeight="600" color="text.secondary">
                            No endpoint requests found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRequests.map((request) => (
                        <TableRow
                          key={request.id}
                          hover
                          onClick={() => handleViewRequest(request)}
                          sx={{
                            transition: "background-color 0.15s ease",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#F1F5F9" },
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" fontWeight="700" color="text.primary">
                              {request.component}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            <Tooltip title={request.task}>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 280,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  color: "text.primary",
                                }}
                              >
                                {request.task}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar
                                sx={{
                                  width: 26,
                                  height: 26,
                                  fontSize: "0.75rem",
                                  backgroundColor: `${primaryColor}20`,
                                  color: primaryColor,
                                  fontWeight: 700,
                                }}
                              >
                                {request.lead.charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography variant="body2" color="text.secondary" fontWeight="500">
                                {request.lead}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            {request.assignedBackend ? (
                              <Tooltip title={request.assignedBackend.email}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Avatar
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      fontSize: "0.7rem",
                                      backgroundColor: "#E2E8F0",
                                      color: "#334155",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {(request.assignedBackend.first_name || request.assignedBackend.username || "D").charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                                    {request.assignedBackend.first_name ? `${request.assignedBackend.first_name}` : request.assignedBackend.username}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            ) : request.assignedBackendEmail ? (
                              <Tooltip title={request.assignedBackendEmail}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Avatar
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      fontSize: "0.7rem",
                                      backgroundColor: "#E2E8F0",
                                      color: "#334155",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {request.assignedBackendEmail.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight="500"
                                    sx={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                  >
                                    {request.assignedBackendEmail}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                Unassigned
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <ModernMethodChip method={request.method} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "'Fira Code', 'Roboto Mono', monospace",
                                  fontSize: "0.78rem",
                                  maxWidth: 160,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  color: request.endpoint ? "text.primary" : "text.secondary",
                                }}
                              >
                                {request.endpoint || "Unassigned"}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            <ModernStatusBadge status={request.status} />
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            <ModernPriorityChip priority={request.priority} />
                          </TableCell>

                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" fontSize="0.8rem" color="text.secondary">
                              {request.requestDate ? new Date(request.requestDate).toLocaleDateString() : "N/A"}
                            </Typography>
                          </TableCell>

                          <TableCell align="center" sx={{ py: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                              <Tooltip title="Code Generator">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCodeSnippetRequest(request);
                                  }}
                                  sx={{
                                    color: "action.active",
                                    "&:hover": { backgroundColor: "action.hover" },
                                  }}
                                >
                                  <CodeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="View Request">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewRequest(request);
                                  }}
                                  sx={{
                                    color: primaryColor,
                                    backgroundColor: `${primaryColor}10`,
                                    "&:hover": { backgroundColor: `${primaryColor}25` },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredRequests.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ borderTop: "1px solid #E2E8F0" }}
              />
            </Paper>
          )}

          {/* Create Dialog */}
          <CreateRequestDialog
            open={openCreateDialog}
            onClose={() => setOpenCreateDialog(false)}
            onSubmit={handleCreateRequest}
          />
        </>
      )}

      {/* View/Edit Dialog */}
      {selectedRequest && (
        <ViewRequestDialog
          open={openViewDialog}
          request={selectedRequest}
          onClose={() => {
            setOpenViewDialog(false);
            setSelectedRequest(null);
          }}
          onUpdate={handleUpdateRequest}
          onDelete={handleDeleteRequest}
        />
      )}

      {/* Global Code Snippet Dialog */}
      {codeSnippetRequest && (
        <CodeSnippetDialog
          open={Boolean(codeSnippetRequest)}
          onClose={() => setCodeSnippetRequest(null)}
          request={codeSnippetRequest}
        />
      )}
    </Box>
  );
};

export default EndpointRequestsPage;
